'use client';

import { GoogleMap, Marker } from '@react-google-maps/api';
import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import { MapPin, Minus, Plus } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useCallback, useMemo, useState } from 'react';

import { useGoogleMapsLoader } from '../../hooks/use-google-maps-loader';
import { cn } from '../../utils/cn';
import { useProfileLayout } from '../layouts/profile-layout-context';
import type { PublicProfileLayout } from '../public-profile-layout';
import {
  resolvePublicProfileModuleChrome,
  type PublicProfileModuleChrome,
} from '../public-profile-module-copy';

/** Use API minimum so zoom-out never increases zoom when config.zoom is below a higher floor. */
const MIN_ZOOM = 0;
const MAX_ZOOM = 20;

/** Legacy editor default — treat as “use layout height”, not a real design height. */
const LEGACY_DEFAULT_HEIGHT = 400;

/** Handover map pane heights (mobile / desktop ≥880). */
const MAP_HEIGHT_CLASS: Record<PublicProfileLayout, string> = {
  cards: 'h-[150px] @min-[880px]/profile:h-[220px]',
  centered: 'h-[140px] @min-[880px]/profile:h-[160px]',
  classic: 'h-[130px] @min-[880px]/profile:h-[180px]',
};

/** Zoom control overlay – same as LocationDetailPage / LocationMapPreview */
function MapZoomControl({
  map,
  zoomInLabel,
  zoomOutLabel,
}: {
  map: google.maps.Map | null;
  zoomInLabel: string;
  zoomOutLabel: string;
}) {
  const zoomIn = useCallback(() => {
    if (!map) return;
    const z = map.getZoom() ?? 16;
    map.setZoom(Math.min(MAX_ZOOM, z + 1));
  }, [map]);
  const zoomOut = useCallback(() => {
    if (!map) return;
    const z = map.getZoom() ?? 16;
    map.setZoom(Math.max(MIN_ZOOM, z - 1));
  }, [map]);
  if (!map) return null;
  return (
    <div className="absolute right-3 top-3 z-10 flex flex-col gap-px overflow-hidden rounded-lg border border-border bg-card/95 shadow-sm backdrop-blur-sm">
      <button
        type="button"
        onClick={zoomIn}
        className="flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-transparent"
        aria-label={zoomInLabel}
      >
        <Plus className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={zoomOut}
        className="flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-transparent"
        aria-label={zoomOutLabel}
      >
        <Minus className="h-4 w-4" />
      </button>
    </div>
  );
}

interface GoogleMapsModuleDisplayProps {
  module: ProfileModuleDto;
  /**
   * Google Maps JavaScript API key. Required to render the map; without it the
   * module shows address details only plus an unavailable placeholder.
   */
  googleMapsApiKey?: string | null;
  chrome?: PublicProfileModuleChrome;
}

type GoogleMapsModuleConfig = {
  type: 'google_maps';
  address: string;
  latitude?: number;
  longitude?: number;
  zoom?: number;
  mapType?: 'roadmap' | 'satellite' | 'hybrid' | 'terrain';
  showMarker?: boolean;
  markerTitle?: string;
  markerDescription?: string;
  height?: number;
  allowFullscreen?: boolean;
};

/** Map style entry for Google Styled Maps */
type MapStyleEntry = {
  elementType?: string;
  featureType?: string;
  stylers: Array<{ color?: string; visibility?: string }>;
};

const HIDE_POIS_STYLES: MapStyleEntry[] = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

const DARK_MAP_STYLES: MapStyleEntry[] = [
  { elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1e293b' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#94a3b8' }] },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#334155' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#0f172a' }],
  },
];

function getGoogleMapsConfig(
  rawConfig: Record<string, unknown>,
): GoogleMapsModuleConfig {
  return {
    type: 'google_maps',
    address: (rawConfig.address as string) || '',
    latitude: rawConfig.latitude as number | undefined,
    longitude: rawConfig.longitude as number | undefined,
    zoom: (rawConfig.zoom as number) || 15,
    mapType:
      (rawConfig.mapType as GoogleMapsModuleConfig['mapType']) || 'roadmap',
    showMarker: rawConfig.showMarker as boolean | undefined,
    markerTitle: rawConfig.markerTitle as string | undefined,
    markerDescription: rawConfig.markerDescription as string | undefined,
    height: (rawConfig.height as number) || LEGACY_DEFAULT_HEIGHT,
    allowFullscreen: rawConfig.allowFullscreen as boolean | undefined,
  };
}

/** Name + address caption under the map (handover MapCard). */
function GoogleMapsLocationDetails({
  layout,
  config,
}: {
  layout: PublicProfileLayout;
  config: GoogleMapsModuleConfig;
}) {
  const title = (config.markerTitle ?? '').trim();
  const addr = (config.address ?? '').trim();
  if (!title && !addr) {
    return null;
  }
  const sameLine = Boolean(title && addr && title === addr);
  const showPin = layout === 'cards';

  return (
    <div
      className={cn(
        'flex text-left',
        // Cards: caption sits in the flush shell card (row padding from handover).
        layout === 'cards' &&
          'items-start gap-3.5 px-[15px] py-[15px] @min-[880px]/profile:gap-3 @min-[880px]/profile:px-5 @min-[880px]/profile:py-[18px]',
        // Centered / classic: caption is inside the module’s own bordered frame.
        layout === 'centered' && 'flex-col p-[15px] @min-[880px]/profile:p-4',
        layout === 'classic' &&
          'flex-col p-[14px] @min-[880px]/profile:px-[18px] @min-[880px]/profile:py-4',
      )}
    >
      {showPin ? (
        <MapPin
          className="mt-px h-[18px] w-[18px] shrink-0"
          style={{ stroke: 'var(--accent)' }}
          strokeWidth={1.7}
          aria-hidden
        />
      ) : null}
      <div className="min-w-0 flex-1">
        {sameLine ? (
          <p className="text-[14px] font-semibold leading-snug text-[var(--text)]">
            {addr}
          </p>
        ) : (
          <>
            {title ? (
              <p className="text-[14px] font-semibold leading-snug text-[var(--text)]">
                {title}
              </p>
            ) : null}
            {addr ? (
              <p
                className={cn(
                  'text-[12.5px] leading-[1.45] text-[var(--muted)]',
                  title && 'mt-[3px]',
                )}
              >
                {addr}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function MapUnavailablePane({ label }: { label: string }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-[var(--surface-muted,#e2e8f0)] px-4 dark:bg-[var(--surface-muted,#1e293b)]">
      <MapPin className="h-8 w-8 text-[var(--muted)]" aria-hidden />
      <p className="text-center text-[12.5px] leading-snug text-[var(--muted)]">
        {label}
      </p>
    </div>
  );
}

/** Renders map via JS API; only mounted when apiKey is present so useJsApiLoader is not called with an empty key. */
function GoogleMapsApiMap({
  apiKey,
  config,
  isDark,
  zoomInLabel,
  zoomOutLabel,
}: {
  apiKey: string;
  config: GoogleMapsModuleConfig;
  isDark: boolean;
  zoomInLabel: string;
  zoomOutLabel: string;
}) {
  const { isLoaded } = useGoogleMapsLoader(apiKey);

  const center = useMemo(
    () =>
      config.latitude != null &&
      config.longitude != null &&
      Number.isFinite(config.latitude) &&
      Number.isFinite(config.longitude)
        ? { lat: config.latitude, lng: config.longitude }
        : null,
    [config.latitude, config.longitude],
  );

  const mapOptions = useMemo(
    () => ({
      zoom: config.zoom ?? 15,
      disableDefaultUI: true,
      zoomControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      scaleControl: false,
      rotateControl: false,
      styles: [...HIDE_POIS_STYLES, ...(isDark ? DARK_MAP_STYLES : [])],
      draggable: false,
      scrollwheel: false,
      disableDoubleClickZoom: true,
      gestureHandling: 'none' as const,
      keyboardShortcuts: false,
    }),
    [config.zoom, isDark],
  );

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const onMapLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  }, []);
  const onMapUnmount = useCallback(() => {
    setMap(null);
  }, []);

  if (!isLoaded) {
    return <div className="h-full w-full" aria-busy />;
  }

  if (!center) {
    return null;
  }

  return (
    <>
      <GoogleMap
        mapContainerClassName="h-full w-full"
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        options={mapOptions}
        zoom={config.zoom ?? 15}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
      >
        {config.showMarker !== false && (
          <Marker
            position={center}
            title={config.markerTitle || config.address || 'Map'}
          />
        )}
      </GoogleMap>
      <MapZoomControl
        map={map}
        zoomInLabel={zoomInLabel}
        zoomOutLabel={zoomOutLabel}
      />
    </>
  );
}

/**
 * Displays the Google Maps module via the Maps JavaScript API only.
 * Without a key, shows an unavailable placeholder (no embed / OSM fallback).
 *
 * Layout chrome follows handover MapCard:
 * - cards: flush map inside ProfileModuleShell (no double padding/radius); caption row
 * - centered / classic: one bordered rounded frame wrapping map + caption
 */
export function GoogleMapsModuleDisplay({
  module,
  googleMapsApiKey,
  chrome,
}: GoogleMapsModuleDisplayProps) {
  const layout = useProfileLayout();
  const copy = chrome ?? resolvePublicProfileModuleChrome(undefined);
  const config = getGoogleMapsConfig(module.config || {});
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const hasCoords =
    config.latitude != null &&
    config.longitude != null &&
    Number.isFinite(config.latitude) &&
    Number.isFinite(config.longitude);
  const hasAddress = (config.address ?? '').trim().length > 0;

  if (!hasAddress && !hasCoords) {
    return null;
  }

  const apiKey = (googleMapsApiKey ?? '').trim();

  // Prefer handover heights; only honor an explicit non-legacy custom height.
  const useCustomHeight =
    config.height != null &&
    config.height !== LEGACY_DEFAULT_HEIGHT &&
    config.height >= 100 &&
    config.height <= 320;
  const mapPaneClass = useCustomHeight ? undefined : MAP_HEIGHT_CLASS[layout];
  const mapPaneStyle = useCustomHeight
    ? { height: `${config.height}px` }
    : undefined;

  const mapPane = hasCoords ? (
    <div
      className={cn('relative w-full overflow-hidden', mapPaneClass)}
      style={mapPaneStyle}
    >
      {apiKey ? (
        <GoogleMapsApiMap
          apiKey={apiKey}
          config={config}
          isDark={isDark}
          zoomInLabel={copy.zoomIn}
          zoomOutLabel={copy.zoomOut}
        />
      ) : (
        <MapUnavailablePane label={copy.mapUnavailable} />
      )}
    </div>
  ) : null;

  const body = (
    <>
      {mapPane}
      <GoogleMapsLocationDetails layout={layout} config={config} />
    </>
  );

  // Cards: outer ProfileModuleShell already owns border/radius/overflow (flush).
  if (layout === 'cards') {
    return <div className="w-full min-w-0">{body}</div>;
  }

  // Centered / classic: single MapCard frame (map flush + padded caption).
  return (
    <div
      className={cn(
        'w-full min-w-0 overflow-hidden border border-[var(--border)]',
        layout === 'centered' && 'rounded-[20px]',
        // Classic mobile handover adds 12px above the map card after seclabel.
        layout === 'classic' &&
          'mt-3 rounded-2xl @min-[880px]/profile:mt-0 @min-[880px]/profile:rounded-[18px]',
      )}
    >
      {body}
    </div>
  );
}
